import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoleLists } from '../shared/slices/rolesSlice';
import { getEnvVar } from '../utils/getEnvVar';
import { useForm } from 'react-hook-form';
import {
  Navigate,
  useBeforeUnload,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { PageContent } from '@deere/fuel-react/PageContent.js';
import { Box, Button } from '@mui/material';
import { useToastProvider } from '../shared/ToastProvider';
import DuplicateStrategyAlert from '../shared/DuplicateStrategyAlert';
import {
  callMasterScopeAPI,
  callMGCMasterAPI,
  callStrategiesGetAPI,
  callUserInfoAPI,
  getSepPlans
} from '../shared/APIHelper';
import { useStatusMessages } from '../shared/StatusMessages';
import { useAPIProgress } from '../shared/APIProgress';
import {
  checkedMgcs,
  checkedScope,
  findUSIndicatorNew,
  getOwnerObj,
  getSpendErrorMsg,
  getUserObj,
  getValueIfPresentElseZero,
  isEventSourceTrusted,
  isScopeAnyGlobalType
} from '../shared/UtilFunctions';
import {
  BEGINNING_PHASE,
  LABELS,
  NOT_AVAILABLE,
  PLAN_STATUS,
  SCOPE_LEVEL_UNITORUNITGROUP,
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DELETED,
  STRATEGY_STATUS_DRAFT,
  SUPPLIER_STATUS_DONOTUSE,
  SUPPLIER_STATUS_LIMITED_USE,
  SUPPLIER_STATUS_REDUCE
} from '../shared/AppConstants';
import NoAccessEmployee from './NoAccessEmployee';
import { useAdobeAnalytics } from '../utils/useAdobeAnalytics';
import { fetchJsonWithAuth } from '../shared/fetch-json.util';
import MessageBanner from '../shared/MessageBanner';
import SMModal from '../shared/SMModal';

import { getFormattedUserNameWithId } from '../shared/UserLogin';
import {
  checkStrategyDuplicateCombination,
  getNewMGCCriteria
} from './StrategyUtils';

import {
  prepareAllMGCSpendData,
  prepareStrategySpendData
} from './SpendChart/SpendUtils';
import { prepareSpendMaps } from './supplier_datagrid/SupplierDataGridUtils';
import {
  findSupplierSpend,
  getSupplierStatus,
  prepareSupplierMap
} from './Supplier/SupplierSpendUtils';
import {
  SPLR_TYPE_CURRENT,
  SPLR_TYPE_NON_CURRENT
} from './supplier_datagrid/prospective_supplier/PSAppConstants';
import { StrategyHeader } from './Strategy/StrategyHeader';
import { StrategyTabs } from './StrategyTabs';
import {
  setActiveTab,
  setIsEdit,
  setSpendGraphData
} from './Strategy/strategySlice';
import {
  hydrateScope,
  resetScopeState,
  selectScopeDirty,
  selectScopeType,
  selectSelectedScopeUnits,
  selectSelectedScopeViewList,
  setScopeDirty as setScopeDirtyInState,
  setScopeInvalidError as setScopeInvalidErrorInState,
  setSelectedScopeViewList as setSelectedScopeViewListInState
} from './Strategy/scopeSlice';
import { useScopeValidation } from './Strategy/useScopeValidation';
import { useUserInfo } from '../shared/UserInfo';
import usePrompt from '../shared/CustomPrompt';
import { useGridApiRef } from '@mui/x-data-grid-pro';

export default function StrategyForm() {
  const strategicSupplierDataGridApiRef = useGridApiRef();
  const nonStrategicSupplierDataGridApiRef = useGridApiRef();
  const info = useUserInfo();
  const navigate = useNavigate();
  const { enqueueToast } = useToastProvider();
  const loggedInUser = info?.userId;
  const loggedInUserName = info?.userName;
  const tokenAvailable = info?.token;
  const isAdmin = info?.isAdmin;
  const isSBM = info?.isSBM;
  const isCommMgr = info?.isCommMgr;
  const hasCreateAccess = info?.hasCreateAccess;
  const [hasAccess, setHasAccess] = useState(null);
  const [sepPlansToReview, setSepPlansToReview] = useState([]);
  const { pushAdobeAnalytics } = useAdobeAnalytics();
  const [readOnly] = useState(false);
  const [masterMGCJson, setMasterMGCJson] = useState(null);
  const {
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    status: rolesStatus
  } = useSelector((state) => state.roles);
  const rolesProgressTracked = useRef(false);
  const [owners, setOwners] = useState(null);
  const [approvers, setApprovers] = useState(null);
  const [userInfoMap, setUserInfoMap] = useState(null);
  const [statusState, setStatusState] = useState('');
  const [selectedMgcAfterEdit, setSelectedMgcAfterEdit] = useState();
  const [selectedMgcOnLoad, setSelectedMgcOnLoad] = useState();
  const [selectedMgcsUpdated, setSelectedMgcsUpdated] = useState(false);
  const [strategyInformation, setStrategyInformation] = useState(null);
  const isEdit = useSelector((state) => state.strategy.isEdit);
  const dispatch = useDispatch();
  const [refresh, setRefresh] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [makeCancelAsLink, setMakeCancelAsLink] = useState(false);
  const handleModalCancel = () => setShowModal(false);
  const handleModalDeleteCancel = () => setShowDeleteModal(false);
  const cancelOKStrategy = () => {
    setMakeCancelAsLink(true);
  };
  const updateSpendGraphData = (data) => {
    dispatch(setSpendGraphData(data));
  };
  const [masterScopeJson, setMasterScopeJson] = useState(null);
  const [mgcDirty, setMgcDirty] = useState(false);
  const [suppliersDirty, setSuppliersDirty] = useState(false);
  const [isStrategyOverviewDirty, setIsStrategyOverviewDirty] = useState(false);
  const [selectedMgc, setSelectedMgc] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState(null);
  const [suppliersMessage, setSuppliersMessage] = useState(null);
  const [selectedMgcList, setSelectedMgcList] = useState(null);
  const [okToPublish, setOkToPublish] = useState(true);
  const [duplicateStrategyList, setDuplicateStrategyList] = useState(null);
  const [strategySupplierUsers, setStrategySupplierUsers] = useState(null);
  const [nonStrategicSuppliersToPersist, setNonStrategicSuppliersToPersist] =
    useState(
      strategyInformation ? strategyInformation.nonStrategySupplierList : []
    );
  const activePhase = useSelector((state) => state.phases?.activePhase);
  const activeTab = useSelector((state) => state.strategy.activeTab);
  const [
    nonStrategicSuppliersDonotUseSpendObj,
    setNonStrategicSuppliersDonotUseSpendObj
  ] = useState();

  const [collapseExpandedSuppliers, setCollapseExpandedSuppliers] =
    useState(false);
  const [scopeUSIndicatorMap, setScopeUSIndicatorMap] = useState(null);
  const [hasUSScope, setHasUSScope] = useState(null);
  const [allMGCSpend, setAllMGCSpend] = useState(null);
  const [strategySave, setStrategySave] = useState(null);
  const [selectedOverviewViewList, setSelectedOverviewViewList] =
    useState(null);
  const selectedScopeUnits = useSelector(selectSelectedScopeUnits);
  const selectedScopeViewList = useSelector(selectSelectedScopeViewList);
  const scopeType = useSelector(selectScopeType);
  const scopeDirty = useSelector(selectScopeDirty);
  const [supplierScopeOverview, setSupplierScopeOverview] = useState(null);
  const prospectiveSuppliersRows = useSelector(
    (state) => state.prospectiveSupplier.prospectiveSupplierRows
  );
  const addRowEnabled = useSelector(
    (state) => state.prospectiveSupplier.addRowEnabled
  );

  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();

  const { strategyId } = useParams();

  let Location = useLocation();
  let id = 0;

  if (strategyId) {
    id = parseInt(strategyId);
  } else if (Location.state?.strategyId) {
    id = Location.state.strategyId;
  }

  const [strategyNumber, setStrategyNumber] = useState(id);
  const [ownerObj, setOwnerObj] = useState(null);

  const { statusMessages, setStatusErrorMsg, clearAllMessages } =
    useStatusMessages();
  const {
    register,
    reset,
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    mode: 'all',
    defaultValues: { scope: null }
  });
  const watchFields = watch(['approverId', 'ownerId']);

  const commonProps = {
    tokenAvailable: tokenAvailable,
    setStatusErrorMsg: setStatusErrorMsg,
    register: register,
    incrementAPIInProgress: incrementAPIInProgress,
    decrementAPIInProgress: decrementAPIInProgress,
    apiInProgressCount: apiInProgressCount,
    strategyNumber: strategyNumber,
    errors: errors,
    control: control,
    isEdit: isEdit,
    watchFields: watchFields,
    masterScopeJson: masterScopeJson,
    reset: reset,
    setValue: setValue,
    getValues: getValues,
    setRefresh: setRefresh,
    switchFromStrategyInfoTabCheck: switchFromStrategyInfoTabCheck
  };

  function handleTabChange(_event, newValue) {
    if (!switchFromStrategyInfoTabCheck()) return;
    if (newValue === 0) setRefresh(true);
    dispatch(setActiveTab(newValue));
  }

  function prepareMGCData(mgcsFromStrategy) {
    let commodity = [];
    let major = [];
    let minorList = [];
    let segmentList = [];

    commodity.push(mgcsFromStrategy[0].commodity);
    major.push(mgcsFromStrategy[0].major);
    mgcsFromStrategy.forEach((record) => {
      if (record.segment) segmentList.push(record.mgccode);
      else if (record.minor) minorList.push(record.mgccode);
    });

    return {
      commodities: commodity,
      majors: major,
      parent: minorList,
      children: segmentList
    };
  }

  function prepareSelectedMGCData(mgcsFromStrategy) {
    let data = [];

    mgcsFromStrategy.forEach((record) => {
      let row = {
        commodity: record.commodity,
        major: record.major,
        minor: record.minor,
        segment: record.segment
      };
      data.push(row);
    });

    return data;
  }

  function checkAndSave(data, event) {
    clearAllMessages();

    let error = validate(data);
    const submitButton = event?.nativeEvent?.submitter?.value;

    if (!error && addRowEnabled) saveStrategy(data, false, submitButton);
    else if (!strategyInformation) setStatusErrorMsg(error);
  }

  function getScope(scopeLevel, tmpSelectedScopeUnits) {
    let { newSelectedParent, newSelectedChild } = checkedScope(
      tmpSelectedScopeUnits || selectedScopeUnits,
      scopeLevel
    );
    return {
      level: scopeLevel,
      unitGroups: newSelectedParent,
      units: newSelectedChild
    };
  }

  function getScopeLevel(scopeValue) {
    if (typeof scopeValue === 'string') {
      return scopeValue;
    }
    return scopeValue?.level;
  }

  function validate(data) {
    let error;
    if (
      selectedMgcsUpdated &&
      (!selectedMgc?.commodity || !selectedMgc?.major)
    ) {
      error = 'MGC required';
      return error;
    }

    let scopeItem =
      getScopeLevel(data.scope) || selectedScopeViewList?.level || scopeType;

    if (scopeItem) {
      let scope = selectedScopeUnits
        ? getScope(scopeItem, selectedScopeUnits)
        : selectedScopeViewList;

      if (
        scope.level === SCOPE_LEVEL_UNITORUNITGROUP &&
        scope.unitGroups.length === 0 &&
        scope.units.length === 0
      ) {
        error = 'Unit or Unit Group required';
        dispatch(
          setScopeInvalidErrorInState('At least 1 Unit must be selected')
        );
        if (strategyInformation)
          dispatch(
            setSelectedScopeViewListInState({
              level: scopeItem,
              unitGroups: [],
              units: []
            })
          );

        return error;
      }
      dispatch(setScopeInvalidErrorInState(null));
    } else error = 'Scope required';
    return error;
  }

  function getSupplierJSON(splrs) {
    return splrs
      ? splrs.map((supplier) => {
          return {
            supplierNumber: supplier.supplierNumber,
            status: getSupplierStatus(supplier),
            comments: supplier.comments,
            statusExceptions: getStatusExceptionsArray(supplier)
          };
        })
      : [];
  }

  function getStatusExceptionsArray(supplier) {
    let statusExceptionsArray = [];
    const mapOfExceptions = supplier?.statusMap;

    if (mapOfExceptions) {
      for (let [key, values] of mapOfExceptions.entries()) {
        values.status &&
          statusExceptionsArray.push({
            unitOrUnitGroupCode: key,
            status: values.status
          });

        if (values?.statusMap) {
          for (let [k, val] of values.statusMap.entries()) {
            statusExceptionsArray.push({ unitOrUnitGroupCode: k, status: val });
          }
        }
      }
    }
    return statusExceptionsArray;
  }

  async function checkDuplicateStrategies(mgcInfo, scopeInfo) {
    try {
      let mgcs = getNewMGCCriteria(mgcInfo);

      return checkStrategyDuplicateCombination(
        mgcs,
        [STRATEGY_STATUS_ACTIVE],
        scopeInfo.level,
        scopeInfo.unitGroups,
        scopeInfo.units,
        strategyNumber,
        commonProps
      );
    } catch (e) {
      // Ignore Error
      console.log(e);
      setStatusErrorMsg(
        "Couldn't validate for Duplicate strategies. Please try again!",
        e
      );
    }
  }

  function getPutOrPostScope(scopeLevel) {
    const scopeObj = getScope(scopeLevel);

    if (
      strategyNumber === 0 ||
      selectedScopeUnits ||
      isScopeAnyGlobalType(scopeLevel)
    )
      return scopeObj;
    else return selectedScopeViewList;
  }

  function getNonStrategicSuppliersToPersist(nonStrategicSuppliersList) {
    //Non-Strategic Supplier payload for explicit persist

    let persistedNonStrategicSuppliers = [];
    nonStrategicSuppliersList?.forEach((supplier) => {
      const status = supplier.status;
      const comments = supplier.comments;

      if (
        (status === SUPPLIER_STATUS_REDUCE && comments?.trim()) ||
        status === SUPPLIER_STATUS_DONOTUSE ||
        status === SUPPLIER_STATUS_LIMITED_USE
      ) {
        persistedNonStrategicSuppliers.push(supplier);
      }
    });

    return persistedNonStrategicSuppliers;
  }

  function putOrPostStrategy(data, strategyStatus) {
    let scopeLevel =
      getScopeLevel(data.scope) || selectedScopeViewList?.level || scopeType;
    let scope = getPutOrPostScope(scopeLevel);
    let mgcs = selectedMgcsUpdated
      ? checkedMgcs(
          selectedMgc.minorSegments,
          selectedMgc.commodity.value,
          selectedMgc.major.value
        )
      : selectedMgcOnLoad;

    let commonPayLoad = {
      currentPhase:
        strategyNumber === 0
          ? BEGINNING_PHASE
          : strategyInformation.currentPhase,
      ownerId: data.ownerId
        ? data.ownerId.value
        : selectedOverviewViewList.ownerId.value,
      approverId: data.approverId
        ? data.approverId.value
        : selectedOverviewViewList.approverId.value,
      scope: scope,
      mgcs: mgcs,
      status: strategyStatus,
      isSetupComplete: true
    };

    let method;
    let body;
    let url = getEnvVar('VITE_GSS_API_URL');

    if (strategyNumber !== 0) {
      url += '/' + strategyNumber;
      method = 'PUT';
      body = {
        ...commonPayLoad,
        strategyId: strategyNumber,
        suppliers: getSupplierJSON(selectedSuppliers),
        nonStrategySupplierList: getNonStrategicSuppliersToPersist(
          nonStrategicSuppliersToPersist
        ),
        nonCurrentProspectiveSuppliers: getProspectiveSuppliersJSONByType(
          SPLR_TYPE_NON_CURRENT
        ),
        currentProspectiveSuppliers:
          getProspectiveSuppliersJSONByType(SPLR_TYPE_CURRENT)
      };
    } else {
      method = 'POST';
      body = {
        ...commonPayLoad
      };
    }
    return { url: url, method: method, body: body };
  }

  function getSupplierSDIS(supplier) {
    let sdis = [];
    if (supplier.sdis?.length > 0) {
      supplier.sdis.forEach((tempSdi) => {
        sdis.push({ sdi: tempSdi });
      });
    }
    return sdis;
  }

  function mapProspectiveSupplier(splr, splrType) {
    const commonProperties = {
      status: splr.status,
      comments: splr.comments,
      keyContactEmail: splr.keyContactEmail,
      companyUrl: splr.companyUrl
    };

    return splrType === SPLR_TYPE_NON_CURRENT
      ? {
          ...commonProperties,
          supplierName: splr.name,
          prospectiveSupplierSDIS: getSupplierSDIS(splr),
          city: splr.city,
          region: splr.region,
          country: splr.country
        }
      : {
          ...commonProperties,
          supplierNumber: splr.number
        };
  }

  function getProspectiveSuppliersJSONByType(splrType) {
    if (prospectiveSuppliersRows?.length > 0) {
      return prospectiveSuppliersRows
        .filter((splr) => splr.type === splrType)
        .map((splr) => mapProspectiveSupplier(splr, splrType));
    }
    return [];
  }

  async function saveStrategy(data, deleteOperation, submitButtonOverride) {
    let submitButton =
      submitButtonOverride || document.activeElement?.value || 'save draft';
    let putOrPost;

    if (deleteOperation) {
      putOrPost = putOrPostStrategy(data, STRATEGY_STATUS_DELETED);
    } else if (
      submitButton === 'publish' ||
      submitButton === 'save' ||
      submitButton === 'save draft'
    ) {
      // Either its Publish Operation or a Save on an Active Strategy
      putOrPost =
        submitButton === 'publish' || submitButton === 'save'
          ? putOrPostStrategy(data, STRATEGY_STATUS_ACTIVE)
          : putOrPostStrategy(data, STRATEGY_STATUS_DRAFT);

      // SDF Check
      let hasUsIndicator = findUSIndicatorNew(
        putOrPost.body.scope,
        scopeUSIndicatorMap
      );
      setHasUSScope(hasUsIndicator);

      let okToProceed =
        !hasUsIndicator ||
        (hasUsIndicator && strategyInformation.sdsFormId !== null);
      if (submitButton !== 'save draft') {
        setOkToPublish(okToProceed);
      }

      // Find duplicate Strategies
      let list = await checkDuplicateStrategies(
        putOrPost.body.mgcs,
        putOrPost.body.scope
      );
      let duplicateCount = list?.length;

      setDuplicateStrategyList(list);

      if (submitButton !== 'save draft') {
        okToProceed = okToProceed && duplicateCount === 0;

        setOkToPublish(okToProceed);

        if (!okToProceed) return; // Can't publish!
      }
    } else {
      setOkToPublish(true);
      putOrPost = putOrPostStrategy(data, STRATEGY_STATUS_DRAFT);
    }

    callSaveStrategy(putOrPost, deleteOperation, submitButton);
  }

  function handleErrorStatus(response) {
    const result = response.json;

    if (response.meta.status === 400)
      setStatusErrorMsg(result['400'] || result.message);
    else if (response.meta.status === 403)
      setStatusErrorMsg(result['403'] || 'Feature is not available');
    else setStatusErrorMsg(JSON.stringify(result));
  }

  function callSaveStrategy(putOrPost, deleteOperation, submitButton) {
    incrementAPIInProgress();
    setCollapseExpandedSuppliers(true);

    fetchJsonWithAuth(putOrPost.url, {
      method: putOrPost.method,
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(putOrPost.body)
    })
      .then((response) => {
        const result = response.json;
        const { strategyId } = result;

        if (strategyId) {
          if (deleteOperation) {
            setShowDeleteModal(null);
            setMakeCancelAsLink(true);
            enqueueToast({
              variant: 'success',
              message: String.raw`Strategy #${strategyNumber} was successfully deleted.`
            });
          } else {
            let tmpMsg = 'published.';

            if (putOrPost.method === 'POST') tmpMsg = 'created.';
            else if (submitButton !== 'publish') tmpMsg = 'updated.';

            enqueueToast({
              variant: 'success',
              message: String.raw`Strategy has been successfully ${tmpMsg}`,
              autoDismiss: 10000
            });
            if (putOrPost.method === 'POST') setStrategyNumber(strategyId);
            clearDirty();

            if (submitButton === 'publish') {
              clearDirtyForPageReload();
              setStrategySave({
                variant: 'success',
                message: String.raw`Strategy has been successfully published.`
              });

              // Goto Edit Strategy
              navigate(
                {
                  pathname: `/editStrategy/${strategyNumber}`,
                  state: {
                    strategy: result
                  }
                },
                { replace: true }
              );
            }
          }
        } else handleErrorStatus(response);
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't save the strategy. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  useEffect(() => {
    if (strategySave) {
      setRefresh(true);
    }
  }, [strategySave]);

  useEffect(() => {
    if (strategyInformation?.strategyId && tokenAvailable) {
      getSepPlans(strategyInformation.strategyId).then((allSepPlans) => {
        setSepPlansToReview(
          allSepPlans.filter((p) => p.planStatus === PLAN_STATUS.SBM_REVIEW)
        );
      });
    }
  }, [strategyInformation, tokenAvailable]);

  function updateSpendObj(
    spendObj,
    growSpend,
    maintainSpend,
    controlSpend,
    reduceSpend,
    donotuseSpend
  ) {
    spendObj.growSpend = growSpend;
    spendObj.maintainSpend = maintainSpend;
    spendObj.controlSpend = controlSpend;
    spendObj.reduceSpend = reduceSpend;
    spendObj.doNotUseSpend = donotuseSpend;
  }

  /*
    prepareSpendDataStructures

    Prepares the temporary spend data based on current strategic suppliers and original strategy spend info
  */
  function prepareSpendDataStructures() {
    let invGrowSpend = 0;
    let invMaintainSpend = 0;
    let invControlSpend = 0;
    let invDonotUseSpend = 0;
    let forecastGrowSpend = 0;
    let forecastMaintainSpend = 0;
    let forecastControlSpend = 0;
    let forecastDonotUseSpend = 0;
    let spendObj;
    let invSpend;
    let frcstSpend;
    const masterSpendObj = prepareSpendMaps(selectedSuppliers);
    let donotUseStatus = SUPPLIER_STATUS_DONOTUSE.toLowerCase();

    selectedSuppliers?.forEach((splr) => {
      spendObj = findSupplierSpend(masterSpendObj, splr);
      invSpend = spendObj.invoiceSpend;
      frcstSpend = spendObj.forecastSpend;

      invGrowSpend += getValueIfPresentElseZero(invSpend.grow);
      forecastGrowSpend += getValueIfPresentElseZero(frcstSpend.grow);
      invMaintainSpend += getValueIfPresentElseZero(invSpend.maintain);
      forecastMaintainSpend += getValueIfPresentElseZero(frcstSpend.maintain);
      invControlSpend += getValueIfPresentElseZero(invSpend.control);
      forecastControlSpend += getValueIfPresentElseZero(frcstSpend.control);
      invDonotUseSpend += getValueIfPresentElseZero(invSpend[donotUseStatus]);
      forecastDonotUseSpend += getValueIfPresentElseZero(
        frcstSpend[donotUseStatus]
      );
    });

    // Add Overall Donot Use Spend
    invDonotUseSpend += nonStrategicSuppliersDonotUseSpendObj?.invAmt;
    forecastDonotUseSpend += nonStrategicSuppliersDonotUseSpendObj?.frcstAmt;

    // Deduce Reduce Spend from original total - strategic spend
    let invReduceSpend =
      strategyInformation.invoiceSpend?.totalSpend -
      invGrowSpend -
      invMaintainSpend -
      invControlSpend -
      invDonotUseSpend;
    let forecastReduceSpend =
      strategyInformation.forecastSpend?.totalSpend -
      forecastGrowSpend -
      forecastMaintainSpend -
      forecastControlSpend -
      forecastDonotUseSpend;

    let invoiceSpend = { ...strategyInformation.invoiceSpend };

    updateSpendObj(
      invoiceSpend,
      invGrowSpend,
      invMaintainSpend,
      invControlSpend,
      invReduceSpend,
      invDonotUseSpend
    );

    let forecastSpend = { ...strategyInformation.forecastSpend };

    updateSpendObj(
      forecastSpend,
      forecastGrowSpend,
      forecastMaintainSpend,
      forecastControlSpend,
      forecastReduceSpend,
      forecastDonotUseSpend
    );
    return { invoiceSpend: invoiceSpend, forecastSpend: forecastSpend };
  }

  useEffect(() => {
    // Strategic Supplier Info changes, prepare Spend Graph data
    if (strategyInformation && nonStrategicSuppliersDonotUseSpendObj) {
      let { invoiceSpend, forecastSpend } = prepareSpendDataStructures();

      prepareStrategySpendData(
        invoiceSpend,
        forecastSpend,
        updateSpendGraphData
      );
    }
  }, [selectedSuppliers, nonStrategicSuppliersDonotUseSpendObj]);

  useEffect(() => {
    // Non-Strategic Supplier Info changes, prepare DonotUse Spend Amounts
    let invAmt = 0;
    let frcstAmt = 0;

    if (
      nonStrategicSuppliersToPersist &&
      nonStrategicSuppliersToPersist.length > 0
    ) {
      let supplierMap;

      nonStrategicSuppliersToPersist?.forEach((nonStrategicSplr) => {
        if (nonStrategicSplr?.status === SUPPLIER_STATUS_DONOTUSE) {
          if (!supplierMap) {
            // Prepare Map
            supplierMap = prepareSupplierMap(strategyInformation);
          }

          const splr = supplierMap.get(nonStrategicSplr.supplierNumber);

          invAmt += splr?.invoiceSpend;
          frcstAmt += splr?.forecastSpend;
        }
      });
    }

    setNonStrategicSuppliersDonotUseSpendObj({
      invAmt: invAmt,
      frcstAmt: frcstAmt
    });
  }, [nonStrategicSuppliersToPersist]);

  function cancelStrategy(e) {
    e.preventDefault();
    if (!readOnly && isFormDirty()) setShowModal(true);
    else setMakeCancelAsLink(true);
  }

  async function deleteOKStrategy(e) {
    e.preventDefault();
    saveStrategy(getValues(), true);
  }

  function deleteStrategy(e) {
    e.preventDefault();
    setShowDeleteModal(true);
  }

  const isFormDirty = () =>
    isStrategyOverviewDirty ||
    mgcDirty ||
    scopeDirty ||
    suppliersDirty ||
    suppliersMessage;

  useBeforeUnload(
    (event) => {
      event.preventDefault();
      event.returnValue = '';
    },
    { when: isFormDirty() }
  );

  usePrompt(showDeleteModal !== null && !showModal && isFormDirty());

  function switchFromStrategyInfoTabCheck() {
    if (activeTab === 0 && isFormDirty()) {
      if (
        window.confirm('You have unsaved changes. Do you want to continue?')
      ) {
        clearDirty();
        return true;
      } else return false;
    }
    return true;
  }

  function loadForm(data) {
    let obj = getUserObj(userInfoMap?.get(data.ownerId), data.ownerId);
    let object = getUserObj(userInfoMap?.get(data.approverId), data.approverId);

    // Clear all dirty flags
    clearDirty();
    setOwnerObj(obj);
    setStatusState(data.status);
    setLastUpdatedBy(
      getFormattedUserNameWithId(
        userInfoMap?.get(data.updatedById),
        data.updatedById
      )
    );
    setSelectedMgcList(prepareMGCData(data.mgcs));
    setSelectedOverviewViewList({
      ownerId: obj,
      approverId: object
    });
    const nextSelectedScopeViewList = {
      level: data.scope.level,
      unitGroups: data.scope.unitGroups,
      units: data.scope.units
    };
    dispatch(
      hydrateScope({
        scopeType: data.scope?.level,
        selectedScopeViewList: nextSelectedScopeViewList,
        selectedScopeUnits: null
      })
    );
    setSelectedMgcOnLoad(prepareSelectedMGCData(data.mgcs));
    setSelectedMgcAfterEdit(selectedMgc);
    setSelectedMgcsUpdated(false);

    setValue('ownerId', obj, { shouldValidate: true });
    setValue('approverId', object, { shouldValidate: true });
    setValue('scope', data.scope?.level, { shouldValidate: true });
  }

  useEffect(() => {
    pushAdobeAnalytics('create strategy', info);
  }, [info]);

  useEffect(() => {
    if (tokenAvailable) {
      setOwnerObj(getOwnerObj(loggedInUser, loggedInUserName));

      dispatch(fetchRoleLists());

      callMGCMasterAPI(
        setMasterMGCJson,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
      callMasterScopeAPI(
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setMasterScopeJson,
        setScopeUSIndicatorMap
      );
    }
  }, [tokenAvailable, dispatch]);

  useEffect(() => {
    // Only increment once per dispatch cycle; guard against the component
    // mounting after another component already started the fetch.
    if (rolesStatus === 'loading' && !rolesProgressTracked.current) {
      rolesProgressTracked.current = true;
      incrementAPIInProgress();
      return;
    }

    const isDone = rolesStatus === 'loaded' || rolesStatus === 'error';

    if (isDone && rolesProgressTracked.current) {
      rolesProgressTracked.current = false;
      decrementAPIInProgress();
    }

    if (rolesStatus === 'error') {
      setStatusErrorMsg(
        'Failed to load the list of owners and approvers. Please refresh the page or try again.',
        null
      );
    }
  }, [rolesStatus]);

  useEffect(() => {
    if (rolesStatus !== 'loaded') return;

    if (strategyAdmins && sroSBMs && sroCommMgrs) {
      if (strategyNumber > 0 && strategySupplierUsers === null) return;

      // Load Owners and Approver Ids -- names
      let map = new Map();

      strategyAdmins.forEach((element) => {
        map.set(element, element);
      });
      sroSBMs.forEach((element) => {
        map.set(element, element);
      });
      sroCommMgrs.forEach((element) => {
        map.set(element, element);
      });

      if (strategySupplierUsers && strategySupplierUsers.length > 0) {
        strategySupplierUsers.forEach((element) => {
          map.set(element, element);
        });
      }

      const userIds = [...map.values()];

      callUserInfoAPI(
        userIds,
        setUserInfoMap,
        null,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
    }
  }, [
    rolesStatus,
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    strategySupplierUsers,
    strategyNumber
  ]);

  useEffect(() => {
    if (userInfoMap) {
      // Load Owners and Approver User Info - complete
      let ownerList = [];
      let approverList = [];

      function getNotAvailableObj(id) {
        return {
          id: id,
          firstName: NOT_AVAILABLE,
          lastName: NOT_AVAILABLE,
          middleInitial: NOT_AVAILABLE
        };
      }

      strategyAdmins.forEach((element) => {
        const user = userInfoMap.get(element) ?? getNotAvailableObj(element);
        ownerList.push(user);
        approverList.push(user);
      });
      sroSBMs.forEach((element) => {
        ownerList.push(userInfoMap.get(element) ?? getNotAvailableObj(element));
      });
      sroCommMgrs.forEach((element) => {
        approverList.push(
          userInfoMap.get(element) ?? getNotAvailableObj(element)
        );
      });
      setOwners(ownerList);
      setApprovers(approverList);
    }
  }, [userInfoMap]);

  useEffect(() => {
    if (info && strategyNumber === 0) {
      setOwnerObj(getOwnerObj(loggedInUser, loggedInUserName));
    }
  }, [info, strategyNumber]);

  useEffect(() => {
    if (strategyNumber > 0 && hasCreateAccess && tokenAvailable) {
      dispatch(setIsEdit(true));

      callStrategiesGetAPI(
        strategyNumber,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setStrategyInformation,
        setSelectedSuppliers,
        setStrategySupplierUsers
      );
      dispatch(setActiveTab(0));
    }
  }, [strategyNumber, tokenAvailable, hasCreateAccess]);

  useEffect(() => {
    if (refresh && strategyNumber > 0 && hasCreateAccess && tokenAvailable) {
      callStrategiesGetAPI(
        strategyNumber,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setStrategyInformation,
        setSelectedSuppliers,
        setStrategySupplierUsers
      );
      setRefresh(false);
    }
  }, [strategyNumber, refresh, tokenAvailable, hasCreateAccess]);

  useEffect(() => {
    // Update strategy info with latest phase
    if (
      strategyInformation &&
      activePhase &&
      strategyInformation.currentPhase !== activePhase
    )
      setStrategyInformation({
        ...strategyInformation,
        currentPhase: activePhase
      });
  }, [activePhase, strategyInformation]);
  useEffect(() => {
    if (strategyInformation) {
      setSupplierScopeOverview(strategyInformation.scope);
      if (userInfoMap) loadForm(strategyInformation);
      setNonStrategicSuppliersToPersist(
        strategyInformation.nonStrategySupplierList
      );
    }
  }, [strategyInformation, userInfoMap, masterMGCJson]);

  useEffect(() => {
    const currentScope = selectedScopeUnits
      ? getScope(scopeType, selectedScopeUnits)
      : selectedScopeViewList;
    setSupplierScopeOverview(currentScope);
  }, [selectedScopeUnits, scopeType, selectedScopeViewList]);

  useScopeValidation({ setValue });

  useEffect(() => {
    //prepare Spend Graph data
    if (strategyInformation) {
      prepareStrategySpendData(
        strategyInformation.invoiceSpend,
        strategyInformation.forecastSpend,
        updateSpendGraphData
      );

      if (strategyInformation.invoiceSpend || strategyInformation.forecastSpend)
        prepareAllMGCSpendData(strategyInformation, setAllMGCSpend);
    }
  }, [strategyInformation]);

  useEffect(() => {
    if (strategyInformation) {
      const currentUser = loggedInUser?.toUpperCase();
      // Require a non-empty logged-in user so undefined === undefined
      // can never grant access; admin override is preserved.
      const isOwnerOrApprover =
        !!currentUser &&
        (strategyInformation.ownerId?.toUpperCase() === currentUser ||
          strategyInformation.approverId?.toUpperCase() === currentUser);

      setHasAccess(isOwnerOrApprover || isAdmin);

      // Find duplicate Strategies
      if (STRATEGY_STATUS_ACTIVE !== strategyInformation.status)
        checkDuplicateStrategies(
          strategyInformation.mgcs,
          strategyInformation.scope
        ).then((list) => setDuplicateStrategyList(list));
    }
  }, [strategyInformation]);

  useEffect(() => {
    if (strategyInformation?.scope && scopeUSIndicatorMap) {
      let hasUsIndicator = findUSIndicatorNew(
        strategyInformation.scope,
        scopeUSIndicatorMap
      );
      setHasUSScope(hasUsIndicator);
    }
  }, [strategyInformation, scopeUSIndicatorMap]);

  function clearDirty() {
    setMgcDirty(false);
    dispatch(setScopeDirtyInState(false));
    setSuppliersDirty(false);
    setSuppliersMessage(null);
    setStrategySave(null);
  }

  function clearDirtyForPageReload() {
    reset(getValues(), {
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false
    });
    clearDirty();
  }

  function resetSelectedMgcAfterEdit(selectedMgcTemp) {
    setSelectedMgcAfterEdit(selectedMgcTemp);
  }

  function setParentMgcData(mgcData, event) {
    setSelectedMgcsUpdated(true);
    setSelectedMgc(mgcData);
    event && setMgcDirty(true);
  }

  function setParentSupplierData(supplierData) {
    setSuppliersDirty(true);
    setSelectedSuppliers(supplierData);
  }

  function setParentNonStrategicSupplierData() {
    setSuppliersDirty(true);
  }

  function setParentNonStrategicToPersist(supplierData) {
    setNonStrategicSuppliersToPersist(supplierData);
  }

  useEffect(() => {
    window.addEventListener('message', receiveMessage, false);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetScopeState());
    };
  }, [dispatch]);

  function receiveMessage(event) {
    if (isEventSourceTrusted(event)) {
      let message = event.data;

      if (message) {
        window.location.reload();
      }
    }
  }

  function paintSaveButton() {
    let jsxFragment = <></>;

    if (isEdit && statusState === STRATEGY_STATUS_DRAFT)
      jsxFragment = (
        <>
          <Button
            variant="secondary"
            sx={(theme) => ({ ml: theme.fuel6Tokens.JddcSpacingSmall })}
            type="submit"
            form="strategy-form"
            value="save draft"
          >
            {LABELS.SAVE_DRAFT}
          </Button>
          <Button
            variant="primary"
            sx={(theme) => ({ ml: theme.fuel6Tokens.JddcSpacingSmall })}
            type="submit"
            form="strategy-form"
            value="publish"
          >
            Publish Strategy
          </Button>
        </>
      );
    else if (isEdit && statusState === STRATEGY_STATUS_ACTIVE)
      jsxFragment = (
        <Button
          variant="primary"
          sx={(theme) => ({ ml: theme.fuel6Tokens.JddcSpacingSmall })}
          type="submit"
          form="strategy-form"
          value="save"
        >
          Save
        </Button>
      );

    return jsxFragment;
  }

  function handlePainting() {
    if (!info) return <></>;

    if (makeCancelAsLink) return <Navigate to="/" />;

    if (!hasCreateAccess || (isEdit && hasAccess === false))
      return <NoAccessEmployee userId={loggedInUser} />;
    else {
      return strategyNumber !== 0 &&
        !strategyInformation &&
        statusMessages?.error ? (
        <MessageBanner
          statusMessages={statusMessages}
          apiInProgressCount={apiInProgressCount}
        />
      ) : null;
    }
  }

  return (
    handlePainting() || (
      <>
        <StrategyHeader
          strategicSupplierDataGridApiRef={strategicSupplierDataGridApiRef}
          nonStrategicSupplierDataGridApiRef={
            nonStrategicSupplierDataGridApiRef
          }
          isEdit={isEdit}
          strategyInformation={strategyInformation}
          deleteStrategy={deleteStrategy}
          cancelStrategy={cancelStrategy}
          hasUSScope={hasUSScope}
          userInfoMap={userInfoMap}
          allMGCSpend={allMGCSpend}
          paintSaveButton={paintSaveButton}
          lastUpdatedBy={lastUpdatedBy}
          strategyId={strategyId}
          onTabChange={handleTabChange}
          statusMessages={statusMessages}
          apiInProgressCount={apiInProgressCount}
          sepPlansToReview={sepPlansToReview}
        />
        <PageContent sx={{ minWidth: 0 }}>
          <Box
            component="form"
            id="strategy-form"
            onSubmit={handleSubmit(checkAndSave)}
          >
            <DuplicateStrategyAlert
              okToPublish={okToPublish}
              duplicateStrategyList={duplicateStrategyList}
              strategyInformation={strategyInformation}
              commonProps={commonProps}
            />
            <StrategyTabs
              strategicSupplierDataGridApiRef={strategicSupplierDataGridApiRef}
              nonStrategicSupplierDataGridApiRef={
                nonStrategicSupplierDataGridApiRef
              }
              strategyInformation={strategyInformation}
              masterMGCJson={masterMGCJson}
              setParentMgcData={setParentMgcData}
              selectedMgc={selectedMgc}
              selectedMgcList={selectedMgcList}
              selectedMgcAfterEdit={selectedMgcAfterEdit}
              resetSelectedMgcAfterEdit={resetSelectedMgcAfterEdit}
              register={register}
              commonProps={commonProps}
              duplicateStrategyList={duplicateStrategyList}
              okToPublish={okToPublish}
              hasUSScope={hasUSScope}
              masterScopeJson={masterScopeJson}
              strategyNumber={strategyNumber}
              ownerObj={ownerObj}
              owners={owners}
              userInfoMap={userInfoMap}
              approvers={approvers}
              isAdmin={isAdmin}
              isSBM={isSBM}
              getSpendErrorMsg={getSpendErrorMsg}
              allMGCSpend={allMGCSpend}
              info={info}
              collapseExpandedSuppliers={collapseExpandedSuppliers}
              setSuppliersDirty={setSuppliersDirty}
              setParentSupplierData={setParentSupplierData}
              setSuppliersMessage={setSuppliersMessage}
              setParentNonStrategicToPersist={setParentNonStrategicToPersist}
              setParentNonStrategicSupplierData={
                setParentNonStrategicSupplierData
              }
              setCollapseExpandedSuppliers={setCollapseExpandedSuppliers}
              supplierScopeOverview={supplierScopeOverview}
              isCommMgr={isCommMgr}
              selectedOverviewViewList={selectedOverviewViewList}
              setSelectedOverviewViewList={setSelectedOverviewViewList}
              suppliersMessage={suppliersMessage}
              setIsStrategyOverviewDirty={() => {
                setIsStrategyOverviewDirty(true);
              }}
            />
          </Box>
          <SMModal
            showModal={showModal}
            handleCancel={handleModalCancel}
            handleYes={cancelOKStrategy}
            title="Changes Will Be Lost"
            msg_line1="Do you want to save your changes before leaving this screen?"
            msg_line2="If you don't save, your changes will be lost."
            cancelText="Cancel"
            yesText="Continue without Saving"
          />
          <SMModal
            showModal={showDeleteModal}
            handleCancel={handleModalDeleteCancel}
            handleYes={deleteOKStrategy}
            title="Delete Strategy?"
            msg_line1="Are you sure you want to delete this strategy?"
            cancelText="Cancel"
            yesText="Delete Strategy"
          />
        </PageContent>
      </>
    )
  );
}
